/**
 * The GAUTO admin routes the panel is allowed to call.
 *
 * This is the single source of truth for both sides: the Pages Function refuses anything not listed
 * here, and the client builds every request from these paths. Upstream routes live under
 * `/v1/admin/`; the panel reaches them as `/api/gauto/<name>`.
 */

export interface GautoEndpoint {
  /** Route after `/api/gauto`. */
  name: string;
  /** Upstream path on the licence Worker. */
  upstream: string;
  /** HTTP methods the panel may use. */
  methods: readonly ('GET' | 'POST')[];
  /** Methods that carry a JSON request body. */
  bodyMethods: readonly ('GET' | 'POST')[];
}

export const GAUTO_ENDPOINTS = [
  { name: 'summary', upstream: '/v1/admin/summary', methods: ['GET'], bodyMethods: [] },
  { name: 'licenses', upstream: '/v1/admin/licenses', methods: ['GET'], bodyMethods: [] },
  { name: 'license', upstream: '/v1/admin/license', methods: ['GET', 'POST'], bodyMethods: ['POST'] },
  { name: 'license/rename', upstream: '/v1/admin/license/rename', methods: ['POST'], bodyMethods: ['POST'] },
  { name: 'license/remove', upstream: '/v1/admin/license/remove', methods: ['POST'], bodyMethods: ['POST'] },
  { name: 'devices', upstream: '/v1/admin/devices', methods: ['GET'], bodyMethods: [] },
  { name: 'devices/reset', upstream: '/v1/admin/devices/reset', methods: ['POST'], bodyMethods: ['POST'] },
  { name: 'device/remove', upstream: '/v1/admin/device/remove', methods: ['POST'], bodyMethods: ['POST'] },
] as const satisfies readonly GautoEndpoint[];

/** Stable client-side paths, e.g. `GAUTO_API.licenses` -> `/api/gauto/licenses`. */
export const GAUTO_API = GAUTO_ENDPOINTS.reduce((paths, endpoint) => {
  paths[endpoint.name] = `/api/gauto/${endpoint.name}`;
  return paths;
}, {} as Record<(typeof GAUTO_ENDPOINTS)[number]['name'], string>);

export function findEndpoint(path: string, method: string): GautoEndpoint | undefined {
  const upper = method.toUpperCase();
  return GAUTO_ENDPOINTS.find(
    (endpoint) => endpoint.upstream === path && (endpoint.methods as readonly string[]).includes(upper),
  );
}

export function requiresBody(endpoint: GautoEndpoint, method: string): boolean {
  return (endpoint.bodyMethods as readonly string[]).includes(method.toUpperCase());
}
